'use client';

import { ReactNode, RefObject, useEffect, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { Chat, ChatRef } from '../Chat/Chat';
import { ChatFile, EmbeddedData, Icon, VersionValue } from '../Chat/NewTypes';
import { Comment } from '@/lib/vodTypes';
import { useVODPlayerContext } from './VODPlayerContext';
import classes from './VODPlayer.module.scss';

const NoSsr = dynamic(() => Promise.resolve(({ children }: { children: ReactNode }) => <>{children}</>), { ssr: false });

interface VODChatPanelProps {
  chat: ChatFile | null;
  comments: Comment[];
}

export function VODChatPanel({ chat, comments }: VODChatPanelProps) {
  const { chatRef, gotoTimestamp, vodId } = useVODPlayerContext();

  const allEmotes = useMemo(() => [
      ...(chat?.embeddedData?.firstParty ?? []),
      ...(chat?.embeddedData?.thirdParty ?? []),
  ], [chat]);

  const badgeMap = useMemo(() => {
      const map = new Map<string, VersionValue>();
      const embeddedData: EmbeddedData = chat?.embeddedData ?? {
          firstParty: [],
          thirdParty: [],
          twitchBadges: [],
          twitchBits: [],
      };
      embeddedData.twitchBadges.forEach(tb => {
          Object.entries(tb.versions).forEach(([version, val]) => {
              map.set(`${tb.name}-${version}`, val);
          });
      });
      return map;
  }, [chat]);

  const emoteMap = useMemo(() => {
      const map = new Map<string, Icon>();
      allEmotes.forEach(e => map.set(e.id, e));
      allEmotes.forEach(e => e.name && map.set(e.name, e));
      return map;
  }, [allEmotes]);

  useEffect(() => {
    if (!chat || (chat.comments ?? []).length <= 0) {
      return;
    }
    comments.forEach(cmt => {
      const chatComments = chat.comments ?? [];
      const chatIndex = chatComments.findIndex(ch => ch.content_offset_seconds >= cmt.timestamp);
      if (chatIndex) {
        const startOffset = Math.max(0, chatIndex - 50);
        const endOffset = Math.min(chatComments.length, chatIndex + 50);
        const relevantComments = chatComments.slice(startOffset, endOffset);
        relevantComments.forEach(rc => {
          rc.message.fragments.forEach(frag => {
            if (frag.emoticon) {
              const emote = emoteMap.get(frag.emoticon.emoticon_id);
              if (emote) {
                  const img = new Image();
                  img.src = `/api/vods/emotes/${vodId}/${emote.id}`;
              }
            }
          });
        });
      }
    });
  }, [chat, comments, vodId, emoteMap]);

  if (!chat) {
    return null;
  }

  return (
    <NoSsr>
      <Chat ref={chatRef as RefObject<ChatRef>} vodId={vodId} chat={chat} onTimestampClicked={gotoTimestamp} badgeMap={badgeMap} emoteMap={emoteMap} />
    </NoSsr>
  );
}

export function VODChatSkeleton() {
  return <div className={classes.chatLoading}>Chat wird geladen...</div>;
}
